"use client";

import { Maybe, Scalars } from "@/types/generated";
import React, { useId, useState } from "react";
import parse from "html-react-parser";
import DOMPurify from "dompurify";
import { useHasHydrated } from "@/hooks/useHasHydrated/useHasHydrated";
import { cn } from "@/lib/utils";
interface RichtextRendererProps {
  content: Maybe<Scalars["String"]["output"]> | string;
  customStyle?: Record<
    string,
    Record<string, string | number | Record<string, string | number>>
  >;
  className?: string;
}

const RichtextRenderer: React.FC<RichtextRendererProps> = ({
  content,
  customStyle,
  className,
}) => {
  const [isStyleReady] = useState(true);
  const hasHydrated = useHasHydrated();

  const uniqueId = useId()?.replaceAll(":", "") ?? "";

  if (!content || !isStyleReady || !hasHydrated) return null;

  const componentSelector = `[data-richtext-id="${uniqueId}"]`;

  /**
   * Check if the provided HTML content is a complete HTML document
   * (includes <html>, <head>, and <body> tags)
   */
  const isFullHtmlDocument = (htmlContent: string): boolean => {
    return (
      htmlContent.includes("<html") &&
      htmlContent.includes("<head") &&
      htmlContent.includes("<body")
    );
  };

  /**
   * Extract and return CSS styles from a full HTML document
   * by scanning <style> tags within the HTML
   */
  const extractStyles = (htmlContent: string): string => {
    if (!isFullHtmlDocument(htmlContent)) return "";

    const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
    let extractedStyles = "";
    let match;
    while ((match = styleRegex.exec(htmlContent)) !== null) {
      extractedStyles += match[1] + "\n";
    }
    return extractedStyles;
  };

  /**
   * Extract only the body section from a full HTML document
   * and remove surrounding tags if necessary
   */
  const extractBodyContent = (htmlContent: string): string => {
    if (!isFullHtmlDocument(htmlContent)) return htmlContent;
    const bodyRegex = /<body[^>]*>([\s\S]*?)<\/body>/i;
    const bodyMatch = bodyRegex.exec(htmlContent);
    if (bodyMatch) {
      return bodyMatch[1];
    }

    // Fallback: extract everything after </head>
    const afterHead = htmlContent.replace(/<head[^>]*>[\s\S]*?<\/head>/i, "");
    return afterHead.replaceAll(/<\/?html[^>]*>|<\/?body[^>]*>/gi, "");
  };

  /**
   * Default scoped CSS applied to the rendered HTML
   * covering basic text, headings, links, code, tables, and media elements
   */
  const defaultStyles = `
    .richtext-wrapper${componentSelector} {
      letter-spacing: var(--letter-spacing-normal);

      /* Headings */
      h1, h2, h3, h4, h5, h6 {
        font-weight: var(--font-semibold);
        margin-bottom: 1rem;
      }

      h1 { font-size: var(--text-3xl); }
      h2 { font-size: var(--text-2xl); }
      h3 { font-size: var(--text-xl); }
      h4 { font-size: var(--text-lg); }
      h5 { font-size: var(--text-md); }
      h6 { font-size: var(--text-sm); }

      /* Basic elements */
      p, blockquote, ul, ol, table, pre, hr {
        margin-bottom: 1rem;
      }

      /* Links */
      a {
        color: var(--text-base-primary);
        &:hover {
          text-decoration: underline;
          color: var(--fill-primary-solid-strong);
        }
      }

      /* Lists */
      ul, ol {
        padding-left: 1.5rem;
        li { margin-bottom: 0.5rem; }
      }
      ul { list-style-type: disc; }
      ol { list-style-type: decimal; }

      /* Code */
      code {
        background-color: var(--fill-base-gray);
        padding: 0.25rem 0.5rem;
        border-radius: var(--radius-input-sm);
      }
      pre {
        background-color: var(--fill-base-gray);
        padding: 1rem;
        border-radius: var(--radius-input-md);
        overflow-x: auto;
        code {
          background-color: transparent;
          padding: 0;
          border-radius: 0;
        }
      }

      /* Table */
      table {
        margin: 1rem 0;
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        border-radius: var(--radius-input-md);
        overflow: auto;
        border: 1px solid var(--stroke-icon-lighter);
        background-color: var(--fill-base-gray);
        color: var(--text-gray-normal);

        thead th {
          border-bottom: 1px solid var(--stroke-icon-lighter);
          font-weight: var(--font-regular);
        }

        tbody tr td,
        tbody tr th {
          border-bottom: 1px solid var(--stroke-icon-lighter);
        }

        tbody tr:last-child td,
        tbody tr:last-child th {
          border-bottom: none;
        }

        th, td {
          padding: 1rem;
        }

        tbody tr th {
          white-space: nowrap;
          font-weight: var(--font-regular);
          span {
            background-color: var(--fill-base-white);
            color: var(--text-base-primary);
          }
        }
      }

      /* Media */
      img, iframe {
        max-width: 100%;
        border-radius: var(--radius-input-md);
        margin: 1rem 0;
      }
      img { height: auto; }
      iframe { aspect-ratio: 16/9; }
    }
  `;

  const camelToKebab = (str: string): string => {
    return str?.replaceAll(/([a-z0-9])([A-Z])/g, "$1-$2")?.toLowerCase() || "";
  };

  /**
   * Generate scoped CSS rules dynamically from the customStyle prop
   * supporting media queries and selector-based customization
   */
  const generateCustomStyles = () => {
    if (!customStyle) return "";

    return Object.entries(customStyle)
      .map(([selector, styles]) => {
        if (typeof styles !== "object" || styles === null) return "";

        const baseStyles: string[] = [];
        const mediaQueries: string[] = [];

        for (const [property, value] of Object.entries(
          styles as Record<string, string | Record<string, string>>,
        )) {
          if (property.startsWith("@media")) {
            const nestedStyles = Object.entries(value as Record<string, string>)
              .map(([prop, val]) => `${camelToKebab(prop)}: ${val};`)
              .join("\n");

            mediaQueries.push(`
            ${property} {
              .richtext-wrapper${componentSelector} ${selector} {
                ${nestedStyles}
              }
            }
          `);
          } else if (typeof value === "object") {
            baseStyles.push(
              `${camelToKebab(property)}: ${JSON.stringify(value)};`,
            );
          } else {
            baseStyles.push(`${camelToKebab(property)}: ${value};`);
          }
        }

        return `
        .richtext-wrapper${componentSelector} ${selector} {
          ${baseStyles.join("\n")}
        }
        ${mediaQueries.join("\n")}
      `;
      })
      .join("\n");
  };

  /**
   * Clean and scope the provided HTML content
   * (extract styles, isolate body content, and sanitize output)
   */
  const isFullDocument = isFullHtmlDocument(content);
  const extractedStyles = isFullDocument ? extractStyles(content) : "";
  const processedContent = isFullDocument
    ? extractBodyContent(content)
    : content;

  // Scope the extracted styles to the component (only if it's a full document)
  const scopedExtractedStyles = extractedStyles
    ? `.richtext-wrapper${componentSelector} {\n${extractedStyles}\n}`
    : "";

  // Use different DOMPurify config based on content type
  const domPurifyConfig = isFullDocument ? { ADD_ATTR: ["target", "rel"] } : {};

  const cleanHTML = DOMPurify.sanitize(processedContent, domPurifyConfig);

  return (
    <>
      <style>{defaultStyles}</style>
      <style>{generateCustomStyles()}</style>
      {scopedExtractedStyles && <style>{scopedExtractedStyles}</style>}
      <div
        className={cn(`richtext-wrapper text-black`,className)}
        data-richtext-id={uniqueId}
      >
        {parse(cleanHTML)}
      </div>
    </>
  );
};

export default RichtextRenderer;
